import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { GetShip } from "../App/Context"
import { classNames } from "../Components"
import { Icon, Icons } from "../Components/Icon"
import { Menu, MenuItemProps } from "../Components/Menu"
import { CloseButton } from "../Components/CloseButton"
import { MenuNMEA } from "../Components/MenuNMEA"
import { MenuSync } from "../Components/MenuSync"
import { GetAppView, AppRoutes } from "./AppView"
import { SearchView } from "./SearchView"
import { GetShipView, ShipRoutes } from "./ShipView"

interface ShipInfoHeaderViewProps {
  actions?: MenuItemProps[];
  customMenus?: React.ReactNode[];
  // The ship toolbar's monitor toggles (#916) -- the anchor/depth watches and, in
  // time, navigation lights. Rendered ahead of the menus so the always-on state
  // reads first, left of the actions.
  //
  // A render callback rather than a node because the two menu blocks below want
  // DIFFERENT forms of it: the narrow one collapses the toggles into a single
  // menu, while the wide one spells them out as a row.
  monitors?: (collapsed: boolean) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  shipHeader?: any;
}

export function ShipInfoHeaderView({ actions, customMenus, monitors, shipHeader: _shipHeader }: ShipInfoHeaderViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const { navigateApp } = GetAppView();
  const { focusActive, navigateShip } = GetShipView();
  const t = app.getT("ShipView");
  const shipContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (shipContainerRef.current) {
        document.documentElement.style.setProperty("--ship-header-height", `${shipContainerRef.current.offsetHeight}px`);
      }
    };
    updateHeaderHeight();
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (shipContainerRef.current) resizeObserver.observe(shipContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [ship, actions, customMenus, focusActive, monitors]);

  // The Focus button carries navigation state the way NMEA/Sync carry theirs
  // (@almute, #916): always blue, outlined while navigation is stopped and filled
  // while it is running. Distinct from `focusActive`, which is only whether the
  // Focus tab is the one on screen — that stays the `selected` ring.
  const navigationActive = useMemo(() => app.status?.navigation == true, [app.status]);

  const headerInfos = useMemo(() => {
    if (!ship) {
      return [];
    }
    const shipData = Ship.shipData(ship);
    return [shipData?.callsign, shipData?.mmsi, shipData?.yachtModelName].filter(Boolean);
  }, [ship]);

  // One list, built once and rendered into both menu blocks. The two are
  // shown/hidden by `AppHeaderView.scss`'s mobile breakpoint rather than by a
  // media query in JS, so both must exist in the DOM -- but their contents were
  // otherwise identical, and keeping two copies of the array meant every addition
  // had to be made twice or silently miss a breakpoint. The monitors are the one
  // thing that genuinely differs, so they take the block's own `collapsed`.
  const menuItems = useCallback((collapsed: boolean) => [
    app.cloudSession() && <CloseButton key="close" className={"row-right"} onClick={() => navigateApp(AppRoutes.accountSettings)} />,
    monitors && <React.Fragment key="monitors">{monitors(collapsed)}</React.Fragment>,
    ...(customMenus || []).map((m, i) => <React.Fragment key={`custom-${i}`}>{m}</React.Fragment>),
    actions && actions.length > 0 && <Menu key="action" button={t("menu_action_title")} buttonClass="button" global={true} items={actions} />,
    !app.cloudSession() && <button key="now" className={classNames(["button", "ShipView-menu-button", navigationActive ? "ShipView-menu-button-filled" : "ShipView-menu-button-outlined", focusActive && "selected"])} onClick={() => focusActive ? history.back() : navigateShip(ShipRoutes.focus)}><Icon icon={Icons.CircleLarge} />{t("menu_now")}</button>,
    <MenuNMEA key="nmea" />,
    <MenuSync key="sync" />,
    (app.hasFeature("search") && <SearchView key="search" />),
  ], [app, actions, customMenus, monitors, focusActive, navigateApp, navigateShip, t]);

  return (
    <div className="ShipView-ship-container" ref={shipContainerRef}>
      <div className="ShipView-header-fill pre"><div className="content"></div></div>
      <div className="ShipView-header">
        <div className="ShipView-title">
          {/* <DetailsToggle /> */}
          <Icon icon={ship?.vesselType == 2 ? Icons.SailingVessel : Icons.MotorVessel} />
          {ship && ship.name}
          <span className="spacer"></span>
          {headerInfos.map((item, i) => <span key={`ship-info-${i}`} className="ShipView-ship-info">{item}</span>)
            .reduce((acc, x) => {
              return !acc ? x : <>{acc}<span className="spacer"></span>{x}</>;
            }, <></>)}
        </div>
        <div className="ShipView-menus">
          <div className="ShipView-menus-mobile">{menuItems(true)}</div>
          <div className="ShipView-menus-desktop">{menuItems(false)}</div>
        </div>
      </div>
      <div className="ShipView-header-fill post"><div className="content"></div></div>
    </div>
  );
}
